import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Agent } from "@/services/types/user";
import { agentsApi } from "@/services/api/agents";

interface AgentState {
  agents: Agent[];
  currentAgent: Agent | null;
  topAgents: Agent[];
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const initialState: AgentState = {
  agents: [],
  currentAgent: null,
  topAgents: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
  },
};

// Async thunks
export const fetchAgents = createAsyncThunk(
  "agents/fetchAgents",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await agentsApi.getAgents(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch agents"
      );
    }
  }
);

export const fetchAgentById = createAsyncThunk(
  "agents/fetchAgentById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await agentsApi.getAgentById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch agent"
      );
    }
  }
);

export const fetchTopAgents = createAsyncThunk(
  "agents/fetchTopAgents",
  async (limit: number = 6, { rejectWithValue }) => {
    try {
      const response = await agentsApi.getTopAgents(limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch top agents"
      );
    }
  }
);

export const searchAgents = createAsyncThunk(
  "agents/searchAgents",
  async (
    { query, params = {} }: { query: string; params?: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await agentsApi.searchAgents(query, params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Search failed");
    }
  }
);

export const fetchAgentLeads = createAsyncThunk(
  "agents/fetchAgentLeads",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentsApi.getAgentLeads();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch leads"
      );
    }
  }
);

const agentSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAgent: (state) => {
      state.currentAgent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch agents
      .addCase(fetchAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = action.payload.agents;
        state.pagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          total: action.payload.pagination.totalResults,
          totalPages: action.payload.pagination.totalPages,
        };
        state.error = null;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch agent by ID
      .addCase(fetchAgentById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgentById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAgent = action.payload;
        state.error = null;
      })
      .addCase(fetchAgentById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch top agents
      .addCase(fetchTopAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTopAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.topAgents = action.payload;
        state.error = null;
      })
      .addCase(fetchTopAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Search agents
      .addCase(searchAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = action.payload.agents;
        state.pagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          total: action.payload.pagination.totalResults,
          totalPages: action.payload.pagination.totalPages,
        };
        state.error = null;
      })
      .addCase(searchAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch agent leads
      .addCase(fetchAgentLeads.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgentLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.currentAgent) {
          state.currentAgent.leads = action.payload.leads || [];
        }
        state.error = null;
      })
      .addCase(fetchAgentLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentAgent } = agentSlice.actions;

// Selectors
export const selectCurrentAgentLeads = (state: { agents: AgentState }) => {
  return state.agents.currentAgent?.leads || [];
};

export default agentSlice.reducer;
